import { FC, useEffect, useState } from "react";
import { Spinner, Alert, Button, Label, TextInput, Select } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { HiPencil, HiX } from "react-icons/hi";
import {
  useGetCompanyDetailsQuery,
  useUpdateCompanyMutation,
  CompanyUpdatePayload,
} from "@/api/companyApi/company";
import {
  formatSectorType,
  formatSectorModel,
  sectorTypeOptions,
  sectorModelOptions,
} from "@/constants/company";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isAdminTierUser } from "@/constants/agent";

const languageOptions = [
  { value: "en", label: "English" },
  { value: "tr", label: "Türkçe" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "nl", label: "Nederlands" },
  { value: "ru", label: "Русский" },
  { value: "ar", label: "العربية" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "hi", label: "हिन्दी" },
];

const formatLanguage = (value?: string) =>
  languageOptions.find((opt) => opt.value === value)?.label ?? value;

const InfoRow: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-sm text-gray-900 dark:text-white md:col-span-2 break-all">
      {value === null || value === undefined || value === "" ? "—" : value}
    </dd>
  </div>
);

const CompanyInfoTab: FC = () => {
  const { t } = useTranslation(["companySetting", "common"]);
  const { data: company, isLoading, isError } = useGetCompanyDetailsQuery();
  const [updateCompany, { isLoading: isSaving }] = useUpdateCompanyMutation();
  const [isEditing, setIsEditing] = useState(false);

  const user = useCurrentUser();
  const canEdit =
    isAdminTierUser(user);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<CompanyUpdatePayload>({
    defaultValues: {
      company_name: "",
      company_website: "",
      company_phone: "",
      country: "",
      sector_type: "",
      sector_model: "",
      company_language: "",
    },
  });

  useEffect(() => {
    if (company) {
      reset({
        company_name: company.company_name ?? "",
        company_website: company.company_website ?? "",
        company_phone: company.company_phone ?? "",
        country: company.country ?? "",
        sector_type: company.sector_type ?? "",
        sector_model: company.sector_model ?? "",
        company_language: company.company_language ?? "",
      });
    }
  }, [company, reset]);

  const onSubmit = async (values: CompanyUpdatePayload) => {
    if (!company?.company_id) return;
    try {
      await updateCompany({ company_id: company.company_id, data: values }).unwrap();
      toast.success(t("companySetting:saveSuccess"));
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err?.data?.message || err?.data?.error || t("companySetting:saveError"));
    }
  };

  const handleCancel = () => {
    if (company) {
      reset({
        company_name: company.company_name ?? "",
        company_website: company.company_website ?? "",
        company_phone: company.company_phone ?? "",
        country: company.country ?? "",
        sector_type: company.sector_type ?? "",
        sector_model: company.sector_model ?? "",
        company_language: company.company_language ?? "",
      });
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !company) {
    return (
      <Alert color="failure" className="mt-4">
        {t("companySetting:loadError")}
      </Alert>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t("companySetting:company.title")}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t("companySetting:company.description")}
          </p>
        </div>
        {canEdit && !isEditing && (
          <Button size="sm" color="blue" onClick={() => setIsEditing(true)}>
            <HiPencil className="w-4 h-4 mr-1" />
            {t("companySetting:company.edit")}
          </Button>
        )}
        {canEdit && isEditing && (
          <Button size="sm" color="gray" onClick={handleCancel} disabled={isSaving}>
            <HiX className="w-4 h-4 mr-1" />
            {t("companySetting:company.cancel")}
          </Button>
        )}
      </div>

      {!isEditing ? (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <dl>
              <InfoRow label={t("companySetting:company.name")} value={company.company_name} />
              <InfoRow label={t("companySetting:company.domain")} value={company.company_domain} />
              <InfoRow label={t("companySetting:company.website")} value={company.company_website} />
              <InfoRow label={t("companySetting:company.phone")} value={company.company_phone} />
              <InfoRow label={t("companySetting:company.country")} value={company.country} />
              <InfoRow
                label={t("companySetting:company.sectorType")}
                value={formatSectorType(company.sector_type)}
              />
              <InfoRow
                label={t("companySetting:company.sectorModel")}
                value={formatSectorModel(company.sector_model)}
              />
              <InfoRow
                label={t("companySetting:company.language")}
                value={formatLanguage(company.company_language)}
              />
            </dl>
          </div>

          {!canEdit && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              {t("companySetting:company.readonlyNote")}
            </p>
          )}
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div>
            <Label htmlFor="company_name" value={t("companySetting:company.name")} />
            <TextInput
              id="company_name"
              placeholder={t("companySetting:company.namePlaceholder")}
              {...register("company_name", {
                required: t("companySetting:required") as string,
              })}
              color={errors.company_name ? "failure" : undefined}
              className="mt-1"
            />
            {errors.company_name && (
              <p className="text-sm text-red-600 mt-1">{errors.company_name.message}</p>
            )}
          </div>

          <div>
            <Label value={t("companySetting:company.domain")} />
            <TextInput value={company.company_domain ?? ""} disabled readOnly className="mt-1" />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t("companySetting:company.domainImmutable")}
            </p>
          </div>

          <div>
            <Label htmlFor="company_website" value={t("companySetting:company.website")} />
            <TextInput
              id="company_website"
              type="url"
              placeholder={t("companySetting:company.websitePlaceholder")}
              {...register("company_website", {
                required: t("companySetting:required") as string,
              })}
              color={errors.company_website ? "failure" : undefined}
              className="mt-1"
            />
            {errors.company_website && (
              <p className="text-sm text-red-600 mt-1">{errors.company_website.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="company_phone" value={t("companySetting:company.phone")} />
            <TextInput
              id="company_phone"
              type="tel"
              placeholder={t("companySetting:company.phonePlaceholder")}
              {...register("company_phone", {
                required: t("companySetting:required") as string,
              })}
              color={errors.company_phone ? "failure" : undefined}
              className="mt-1"
            />
            {errors.company_phone && (
              <p className="text-sm text-red-600 mt-1">{errors.company_phone.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="country" value={t("companySetting:company.country")} />
            <TextInput
              id="country"
              placeholder={t("companySetting:company.countryPlaceholder")}
              {...register("country", {
                required: t("companySetting:required") as string,
              })}
              color={errors.country ? "failure" : undefined}
              className="mt-1"
            />
            {errors.country && (
              <p className="text-sm text-red-600 mt-1">{errors.country.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="sector_type" value={t("companySetting:company.sectorType")} />
            <Controller
              name="sector_type"
              control={control}
              rules={{ required: t("companySetting:required") as string }}
              render={({ field }) => (
                <Select
                  id="sector_type"
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  color={errors.sector_type ? "failure" : undefined}
                  className="mt-1"
                >
                  <option value="" disabled>
                    {t("companySetting:company.selectSector")}
                  </option>
                  {sectorTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              )}
            />
            {errors.sector_type && (
              <p className="text-sm text-red-600 mt-1">{errors.sector_type.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="sector_model" value={t("companySetting:company.sectorModel")} />
            <Controller
              name="sector_model"
              control={control}
              rules={{ required: t("companySetting:required") as string }}
              render={({ field }) => (
                <Select
                  id="sector_model"
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  color={errors.sector_model ? "failure" : undefined}
                  className="mt-1"
                >
                  <option value="" disabled>
                    {t("companySetting:company.selectSectorModel")}
                  </option>
                  {sectorModelOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              )}
            />
            {errors.sector_model && (
              <p className="text-sm text-red-600 mt-1">{errors.sector_model.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="company_language" value={t("companySetting:company.language")} />
            <Controller
              name="company_language"
              control={control}
              rules={{ required: t("companySetting:required") as string }}
              render={({ field }) => (
                <Select
                  id="company_language"
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  color={errors.company_language ? "failure" : undefined}
                  className="mt-1"
                >
                  <option value="" disabled>
                    {t("companySetting:company.selectLanguage")}
                  </option>
                  {languageOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              )}
            />
            {errors.company_language && (
              <p className="text-sm text-red-600 mt-1">{errors.company_language.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button color="gray" onClick={handleCancel} disabled={isSaving} type="button">
              {t("companySetting:company.cancel")}
            </Button>
            <Button type="submit" color="blue" disabled={isSaving || !isDirty}>
              {isSaving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {t("companySetting:company.saving")}
                </>
              ) : (
                t("companySetting:company.save")
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CompanyInfoTab;
